import type { LocalizedDict } from '../index'

// Settings → Store integrations (RealStoreIntegrations.tsx). Text that the
// shared seller stores dictionary (i18n/seller/stores.ts) does not cover.
export type RealStoreIntegrationsStrings = {
  amazonRegions: {
    na: string
    eu: string
    fe: string
  }
  backfillWindows: {
    '30d': string
    '90d': string
    '1y': string
    all: string
  }
  toasts: {
    intakeEnabled: string
    intakeDisabled: string
    intakeRefused: string
    preflightFailed: string
    preflightFailedDescription: string
    storeRemoved: string
    removeFailed: string
    connectionFailed: (provider: string) => string
    etsyConnectFailed: string
    providerConnectFailed: (provider: string) => string
    syncWindowFailed: string
    historySyncStarted: string
    historySyncFailed: string
  }
  completeEtsyAuth: string
  removeUnconnectedConfirm: string
  removing: string
  remove: string
  cancel: string
  connecting: string
  importPastOrders: string
  orderHistoryWindow: string
  syncing: string
  syncHistory: string
  preflight: {
    title: string
    hint: string
    token: string
    present: string
    missing: string
    fulfillment: string
    ready: string
    inactive: string
    mappedSkus: string
    parkedOrders: string
    confirmEnable: string
  }
  connectProvider: (name: string) => string
  etsyRedirect: string
  connectEtsy: string
  shopifyDomain: string
  connectShopify: string
  ebayRedirect: string
  connectEbay: string
  amazonRegion: string
  connectAmazon: string
}

export const realStoreIntegrationsI18n: LocalizedDict<RealStoreIntegrationsStrings> = {
  en: {
    amazonRegions: {
      na: 'North America (US, CA, MX, BR)',
      eu: 'Europe (UK, DE, FR, IT, ES, …)',
      fe: 'Far East (JP, AU, SG)',
    },
    backfillWindows: {
      '30d': 'Last 30 days',
      '90d': 'Last 90 days',
      '1y': 'Last year',
      all: 'All time',
    },
    toasts: {
      intakeEnabled: 'Order intake enabled',
      intakeDisabled: 'Order intake disabled',
      intakeRefused: 'Order intake change refused',
      preflightFailed: 'Preflight failed',
      preflightFailedDescription: 'Could not verify whether this store is ready to receive orders.',
      storeRemoved: 'Store removed',
      removeFailed: 'Could not remove store',
      connectionFailed: (provider) => `${provider} connection failed`,
      etsyConnectFailed: 'Could not start Etsy connect',
      providerConnectFailed: (provider) => `Could not start ${provider} connect`,
      syncWindowFailed: 'Could not update sync window',
      historySyncStarted: 'Order history sync started',
      historySyncFailed: 'Could not start history sync',
    },
    completeEtsyAuth: 'Complete Etsy authorization to start syncing this store.',
    removeUnconnectedConfirm: 'Remove this unconnected store?',
    removing: 'Removing…',
    remove: 'Remove',
    cancel: 'Cancel',
    connecting: 'Connecting…',
    importPastOrders: 'Import past orders',
    orderHistoryWindow: 'Order history window',
    syncing: 'Syncing…',
    syncHistory: 'Sync history',
    preflight: {
      title: 'Activation preflight',
      hint: 'The switch turns on when you confirm this check.',
      token: 'Token:',
      present: 'present',
      missing: 'missing',
      fulfillment: 'Fulfillment:',
      ready: 'ready',
      inactive: 'inactive',
      mappedSkus: 'Mapped SKUs:',
      parkedOrders: 'Parked orders:',
      confirmEnable: 'Confirm enable',
    },
    connectProvider: (name) => `Connect ${name}`,
    etsyRedirect:
      'You will be redirected to Etsy to authorize Pexify. Sign in with the Etsy account that owns your shop — your shop name and ID are detected automatically.',
    connectEtsy: 'Connect Etsy',
    shopifyDomain: 'Shopify domain',
    connectShopify: 'Connect Shopify',
    ebayRedirect:
      'You will be redirected to eBay to authorize Pexify. Sign in with the eBay account that owns your store.',
    connectEbay: 'Connect eBay',
    amazonRegion: 'Amazon region',
    connectAmazon: 'Connect Amazon',
  },
  tr: {
    amazonRegions: {
      na: 'Kuzey Amerika (US, CA, MX, BR)',
      eu: 'Avrupa (UK, DE, FR, IT, ES, …)',
      fe: 'Uzak Doğu (JP, AU, SG)',
    },
    backfillWindows: {
      '30d': 'Son 30 gün',
      '90d': 'Son 90 gün',
      '1y': 'Son 1 yıl',
      all: 'Tüm zamanlar',
    },
    toasts: {
      intakeEnabled: 'Sipariş akışı açıldı',
      intakeDisabled: 'Sipariş akışı kapatıldı',
      intakeRefused: 'Sipariş akışı değiştirilemedi',
      preflightFailed: 'Ön kontrol başarısız',
      preflightFailedDescription: 'Bu mağazanın sipariş almaya hazır olup olmadığı doğrulanamadı.',
      storeRemoved: 'Mağaza kaldırıldı',
      removeFailed: 'Mağaza kaldırılamadı',
      connectionFailed: (provider) => `${provider} bağlantısı başarısız`,
      etsyConnectFailed: 'Etsy bağlantısı başlatılamadı',
      providerConnectFailed: (provider) => `${provider} bağlantısı başlatılamadı`,
      syncWindowFailed: 'Senkron aralığı güncellenemedi',
      historySyncStarted: 'Geçmiş sipariş senkronu başladı',
      historySyncFailed: 'Geçmiş senkronu başlatılamadı',
    },
    completeEtsyAuth: 'Bu mağazayı senkronlamaya başlamak için Etsy yetkilendirmesini tamamla.',
    removeUnconnectedConfirm: 'Bağlanmamış bu mağaza kaldırılsın mı?',
    removing: 'Kaldırılıyor…',
    remove: 'Kaldır',
    cancel: 'Vazgeç',
    connecting: 'Bağlanıyor…',
    importPastOrders: 'Geçmiş siparişleri al',
    orderHistoryWindow: 'Sipariş geçmişi aralığı',
    syncing: 'Senkronlanıyor…',
    syncHistory: 'Geçmişi senkronla',
    preflight: {
      title: 'Açılış ön kontrolü',
      hint: 'Bu kontrolü onayladığında anahtar açılır.',
      token: 'Token:',
      present: 'var',
      missing: 'yok',
      fulfillment: 'Gönderim:',
      ready: 'hazır',
      inactive: 'pasif',
      mappedSkus: 'Eşlenmiş SKU:',
      parkedOrders: 'Bekletilen sipariş:',
      confirmEnable: 'Onayla ve aç',
    },
    connectProvider: (name) => `${name} bağla`,
    etsyRedirect:
      'Pexify’ı yetkilendirmen için Etsy’ye yönlendirileceksin. Mağazanın sahibi olan Etsy hesabıyla giriş yap — mağaza adın ve kimliğin otomatik algılanır.',
    connectEtsy: 'Etsy bağla',
    shopifyDomain: 'Shopify alan adı',
    connectShopify: 'Shopify bağla',
    ebayRedirect:
      'Pexify’ı yetkilendirmen için eBay’e yönlendirileceksin. Mağazanın sahibi olan eBay hesabıyla giriş yap.',
    connectEbay: 'eBay bağla',
    amazonRegion: 'Amazon bölgesi',
    connectAmazon: 'Amazon bağla',
  },
}
